import React from 'react';
import { TableCell, TableRow } from '@mui/material';
import { PropertyTaxState } from '../../../../../../types/properties/propertyTypes';
import DotIcon from '../../../../../components/DotIcon';
import Menu from '../../../../components/Menu';
import Tooltip from '../../../../../../muiComponents/Tooltip';
import useTranslate from '../../../../../../customHooks/useTranslate';
import Icon from '../../../../../../muiComponents/Icon';
import {
    getSupplierTaxCalculationRuleTypeSubTypes,
    supplierTaxCalculationRuleTypeFrequencyIcons,
    supplierTaxCalculationRuleTypeSubjectIcons,
    supplierTaxCalculationTypesIcons,
    supplierTaxTypesIcons,
    TAX_DATE_TAG_COLOR,
    UnknownOptionIcons,
} from '../../../../../../utils/taxes/taxesListUtils';
import {
    categorizePeriod,
    formatDate,
    isDateless,
} from '../../../../../../utils/formatDates';
import Tag from '../../../../../../muiComponents/Tag';
import routes from '../../../../../../utils/commonVariables/routes';
import encodeBase64 from '../../../../../../utils/encodeBase64';

interface PropertyTaxRowProps {
    tax: PropertyTaxState;
}
const PropertyTaxRow = ({ tax }: PropertyTaxRowProps) => {
    const translate = useTranslate();

    const supplierTaxTypeIcon =
        supplierTaxTypesIcons[
            tax.taxTypeKey as keyof typeof supplierTaxTypesIcons
        ];
    const supplierTaxCalculationTypeIcon =
        supplierTaxCalculationTypesIcons[
            tax.taxCalculationTypeKey as keyof typeof supplierTaxCalculationTypesIcons
        ];

    const supplierTaxCalculationRuleTypeSubIcons =
        getSupplierTaxCalculationRuleTypeSubTypes(
            tax.taxCalculationRuleTypeKey
        );

    const supplierTaxCalculationRuleTypeSubjectIcon =
        supplierTaxCalculationRuleTypeSubIcons.subject
            ? supplierTaxCalculationRuleTypeSubjectIcons[
                  supplierTaxCalculationRuleTypeSubIcons.subject as keyof typeof supplierTaxCalculationRuleTypeSubjectIcons
              ]
            : UnknownOptionIcons.SUPPLIER_TAX_CALCULATION_RULE_SUBJECT_TYPE;

    const supplierTaxCalculationRuleTypeFrequencyIcon =
        supplierTaxCalculationRuleTypeSubIcons.frequency
            ? supplierTaxCalculationRuleTypeFrequencyIcons[
                  supplierTaxCalculationRuleTypeSubIcons.frequency as keyof typeof supplierTaxCalculationRuleTypeFrequencyIcons
              ]
            : UnknownOptionIcons.SUPPLIER_TAX_CALCULATION_RULE_FREQUENCY_TYPE;
    const isDatelessTax = isDateless(tax.fromDate) && isDateless(tax.toDate);
    const tagText = categorizePeriod(tax.fromDate, tax.toDate);

    return (
        <TableRow key={tax.id}>
            <TableCell>
                <div className='flex-center'>
                    <DotIcon truthy={tax.enabled} />
                </div>
            </TableCell>
            <TableCell>{tax.name}</TableCell>
            <TableCell>{tax.room}</TableCell>
            <TableCell>{tax.mealPlan}</TableCell>
            <TableCell>{tax.ratePlan}</TableCell>
            <TableCell>
                <div className='flex gap-10 '>
                    <Tooltip
                        title={`${translate('type')}: ${tax.taxType || ''}`}
                    >
                        <span className='flex gap-5 flex-align-center'>
                            <Icon iconName={supplierTaxTypeIcon} />
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={`${translate('calculation_type')}: ${
                            tax.taxCalculationType || ''
                        }`}
                    >
                        <span className='flex gap-5 flex-align-center'>
                            <Icon iconName={supplierTaxCalculationTypeIcon} />
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={`${translate('applies_by')}: ${
                            supplierTaxCalculationRuleTypeSubIcons.subject || ''
                        }`}
                    >
                        <span className='flex gap-5 flex-align-center'>
                            <Icon
                                iconName={
                                    supplierTaxCalculationRuleTypeSubjectIcon
                                }
                            />
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={`${translate('frequency')}: ${
                            supplierTaxCalculationRuleTypeSubIcons.frequency ||
                            ''
                        }`}
                    >
                        <span className='flex gap-5 flex-align-center'>
                            <Icon
                                iconName={
                                    supplierTaxCalculationRuleTypeFrequencyIcon
                                }
                            />
                        </span>
                    </Tooltip>
                </div>
            </TableCell>
            <TableCell>
                {!isDatelessTax ? (
                    <div
                        className='flex-column flex-align-end gap-10 height100perc flex-between'
                        style={{ minWidth: 170 }}
                    >
                        {formatDate(tax.fromDate)} - {formatDate(tax.toDate)}
                        <Tag
                            text={tagText}
                            color={TAX_DATE_TAG_COLOR[tagText]}
                        />
                    </div>
                ) : (
                    ''
                )}
            </TableCell>
            <TableCell>
                <Menu
                    menuItems={[
                        {
                            text: 'go_to_tax_detail',
                            icon: 'OpenInNew',
                            onClick: () => {
                                const route = `${routes.landingPrivate}${
                                    routes.taxes
                                }?id=${encodeBase64(tax.id)}`;
                                window.open(route, route);
                            },
                        },
                    ]}
                />
            </TableCell>
        </TableRow>
    );
};

export default PropertyTaxRow;
